// Global Instructions Rule Applied! - 2026-05-06

import { z } from 'zod'

/**
 * Canonical resolved-column shape. Produced once at the cfg → grid hydration boundary
 * by `reportingColumnToViewConfigColumn`, then passed unchanged to the AntD column
 * builder, EditColumns drawer, Sort/Filter panels, Add-columns catalog, and Excel
 * exporter. There is no intermediate snake-case row shape — adding a new column
 * field means editing `ViewConfigColumn` and the converter, and the TypeScript
 * compiler then flags every consumer that needs to handle it.
 */
export interface ViewConfigColumn {
  columnTitle: string
  /** Stable column id (cfg `field_name`) — prefs, column layout, React keys. */
  valueKey: string
  /**
   * Record path for the raw / stored value when different from `valueKey`
   * (cfg `field_value_ref` when set, else `field_name`). Sorting prefers
   * `sortKey` over this.
   */
  dataKey?: string
  fieldType: string
  /** Source module (e.g. deals, accounts) — used for Add columns tab grouping. */
  moduleKey?: string
  minWidth?: number
  freezeColumn?: boolean
  filterable?: boolean
  sortable?: boolean
  /** Cfg `filter_type` — drives filter operator UI (range / select / contact / custom). */
  filterType?: string
  /** API key for filter option lists (TableView column `filterKey`). */
  filterKey?: string
  /**
   * Sort key sent to the RPC (cfg `sort_key`). When set, takes precedence over
   * `dataKey` / `valueKey` for sort. Some columns sort on a different field than
   * they display (Industry sorts on the label, not the UUID; Tranche sorts on
   * `date_invested`).
   */
  sortKey?: string
  /** Display/label path on the record when raw data uses `dataKey` (cfg `field_name` when `field_value_ref` is set). */
  labelKey?: string
  tagDisplay?: boolean
  required?: boolean
  editable?: boolean
  /** Link target / kind from cfg `link_type` when `linkable` is true (e.g. 'open-record-drawer', 'open-record-full'). */
  fieldKey?: string
  /** Related-record link target: '<table>__id' field on the row (e.g.
   *  'funds__id') - tables open that record instead of their own module's. */
  linkIdField?: string
  isDefaultColumn?: boolean
}

/**
 * Dev-time runtime schema. Parsed inside the cfg → ViewConfigColumn converter when
 * NODE_ENV === 'development' to fail loud if a field gets dropped. Production stays
 * a plain typed object — zero runtime cost.
 */
export const ViewConfigColumnSchema = z
  .object({
    columnTitle: z.string(),
    valueKey: z.string(),
    dataKey: z.string().optional(),
    fieldType: z.string(),
    moduleKey: z.string().optional(),
    minWidth: z.number().optional(),
    freezeColumn: z.boolean().optional(),
    filterable: z.boolean().optional(),
    sortable: z.boolean().optional(),
    filterType: z.string().optional(),
    filterKey: z.string().optional(),
    sortKey: z.string().optional(),
    labelKey: z.string().optional(),
    tagDisplay: z.boolean().optional(),
    required: z.boolean().optional(),
    editable: z.boolean().optional(),
    fieldKey: z.string().optional(),
    linkIdField: z.string().nullish(),
    isDefaultColumn: z.boolean().optional()
  })
  .strict()
